import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import {
  AdministrationService,
  type CatalogAddon,
} from '@services/requests/housien-services/AdministrationService';
import type { AddonItem } from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseSubscriptionAddonsResult {
  addons: AddonItem[];
  catalog: CatalogAddon[];
  isLoading: boolean;
  isError: boolean;
  purchase: (addonSlug: string) => Promise<void>;
}

/**
 * Fetches subscription addons (purchased + catalog) for an agency.
 *
 * Wraps GET /agencies/{agencyId}/subscription/addons for purchased list
 * and GET /administration/addons for the available catalog.
 * Starts with isLoading=true to prevent empty-state flash (dls-001).
 */
export function useSubscriptionAddons(agencyId: string): UseSubscriptionAddonsResult {
  const [addons, setAddons] = useState<AddonItem[]>([]);
  const [catalog, setCatalog] = useState<CatalogAddon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchAddons = useCallback(() => {
    setIsLoading(true);
    setIsError(false);

    Promise.allSettled([
      AgenciesService.listSubscriptionAddons(agencyId),
      AdministrationService.getAddons(),
    ])
      .then(([purchasedResult, catalogResult]) => {
        if (purchasedResult.status === 'fulfilled') {
          setAddons(purchasedResult.value);
        }
        if (catalogResult.status === 'fulfilled') {
          setCatalog(catalogResult.value);
        } else {
          setIsError(true);
        }
        setIsLoading(false);
      });
  }, [agencyId]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  const purchase = useCallback(
    async (addonSlug: string) => {
      await AgenciesService.purchaseSubscriptionAddon(agencyId, addonSlug);
      fetchAddons();
    },
    [agencyId, fetchAddons],
  );

  return { addons, catalog, isLoading, isError, purchase };
}
